import { Pressable, View } from "react-native"
import { SafeAreaView } from "react-native-safe-area-context"
import { sharedStyles, trpc } from "@src/utils"
import { Text, Header, AnnouncementForm, BrandForm, CollectionForm, List, SectionForm, MCIcon, ActivityIndicator } from "@src/components"
import { Show, useObservable } from "@legendapp/state/react"
import { ar } from "@src/i18n"
import { Href } from "expo-router"
import { AnnouncementCard, BrandCard, SectionCard } from "@src/components/menu"

type State =
  | { type: "announcement" }
  | {
      type: "announcement-edit"
      id: number
      initialValues: { title?: string; description?: string; entity: "product" | "section" | "collection"; entityId: number; status: "active" | "inactive" }
    }
  | { type: "brand" }
  | { type: "brand-edit"; id: number; initialValues: { name: string } }
  | { type: "section" }
  | { type: "section-edit"; id: number; initialValues: { name: string; status: "active" | "inactive" } }
  | { type: "collection"; sectionId: number }
  | { type: "collection-edit"; id: number; sectionId: number; initialValues: { name: string; sectionId: number; status: "active" | "inactive" } }

export default function Menu() {
  const menu = trpc.menu.get.useQuery()
  const state$ = useObservable<State | undefined>(undefined)
  const utils = trpc.useUtils()

  const header = (
    <Header
      title={ar.menu}
      right={
        <Pressable className="h-full p-2 disabled:opacity-30" onPress={() => menu.refetch()} disabled={menu.isRefetching || menu.isLoading}>
          <MCIcon name="refresh" size={24} color="black" />
        </Pressable>
      }
    />
  )

  if (menu.isLoading) {
    return (
      <>
        {header}
        <ActivityIndicator className="mt-4" />
      </>
    )
  }

  return (
    <>
      {header}
      <List
        ListHeaderComponent={
          <>
            <View className="bg-white flex-row items-center justify-between mb-2 w-full px-2">
              <Text className="text-lg font-bold">{ar.announcements}</Text>
              <Pressable className="p-2" onPress={() => state$.set({ type: "announcement" })}>
                <MCIcon name="plus" size={24} color="black" />
              </Pressable>
            </View>

            {menu.data?.announcements.length === 0 ? (
              <View className="mb-4">
                <Text className="text-center">{ar.noAnnouncements}</Text>
              </View>
            ) : null}

            <View className="flex-row flex-wrap justify-center">
              {menu.data?.announcements.map((announcement) => {
                const image = announcement.images?.at(-1)
                const entity = announcement.entity
                const entityId = announcement.entityId
                const isArchived = announcement.status === "inactive"

                const href = (() => {
                  if (entity === "section") return `/dashboard/menu/section/${entityId}`
                  if (entity === "collection") return `/dashboard/menu/collection/${entityId}`
                  if (entity === "product") return `/dashboard/products/${entityId}`
                })() as Href

                return (
                  <AnnouncementCard
                    key={announcement.id.toString()}
                    announcement={announcement}
                    href={href}
                    image={image}
                    isArchived={isArchived}
                    onRefresh={() => utils.menu.get.invalidate()}
                    onEdit={() =>
                      state$.set({
                        type: "announcement-edit",
                        id: announcement.id,
                        initialValues: {
                          title: announcement.title ?? undefined,
                          description: announcement.description ?? undefined,
                          entity: announcement.entity,
                          entityId: announcement.entityId,
                          status: announcement.status,
                        },
                      })
                    }
                  />
                )
              })}
            </View>

            <View className="bg-white flex-row items-center justify-between mb-2 w-full px-2">
              <Text className="text-lg font-bold">{ar.brands}</Text>
              <Pressable className="p-2" onPress={() => state$.set({ type: "brand" })}>
                <MCIcon name="plus" size={24} color="black" />
              </Pressable>
            </View>

            {menu.data?.brands?.length === 0 ? (
              <View className="mb-4">
                <Text className="text-center">{ar.noBrands}</Text>
              </View>
            ) : null}

            <View className="flex-row flex-wrap">
              {menu.data?.brands?.map((brand) => (
                <BrandCard
                  key={brand.id.toString()}
                  brand={brand}
                  onRefresh={() => utils.menu.get.invalidate()}
                  onEdit={() =>
                    state$.set({
                      type: "brand-edit",
                      id: brand.id,
                      initialValues: { name: brand.name },
                    })
                  }
                />
              ))}
            </View>

            <View className="bg-white flex-row items-center justify-between mb-2 w-full px-2">
              <Text className="text-lg font-bold">{ar.sections}</Text>
              <Pressable className="p-2" onPress={() => state$.set({ type: "section" })}>
                <MCIcon name="plus" size={24} color="black" />
              </Pressable>
            </View>
          </>
        }
        ListEmptyComponent={
          <View className="mb-4">
            <Text className="text-center">{ar.noSections}</Text>
          </View>
        }
        data={menu.data?.sections || []}
        onRefresh={() => menu.refetch()}
        contentContainerStyle={sharedStyles.contentContainer}
        renderItem={({ item }) => (
          <SectionCard
            section={item}
            onAddCollection={() => state$.set({ type: "collection", sectionId: item.id })}
            onRefresh={menu.refetch}
            onEdit={() =>
              state$.set({
                type: "section-edit",
                id: item.id,
                initialValues: { name: item.name, status: item.status },
              })
            }
            onEditCollection={(collection) =>
              state$.set({
                type: "collection-edit",
                id: collection.id,
                sectionId: collection.sectionId,
                initialValues: { name: collection.name, sectionId: collection.sectionId, status: "active" },
              })
            }
          />
        )}
        ListFooterComponent={<SafeAreaView />}
      />

      <Show if={() => state$.get()?.type === "announcement" || state$.get()?.type === "announcement-edit"}>
        {() => {
          const s = state$.get()
          if (s?.type === "announcement-edit") {
            return <AnnouncementForm onClose={() => state$.set(undefined)} id={s.id} initialValues={s.initialValues} />
          }
          return <AnnouncementForm onClose={() => state$.set(undefined)} />
        }}
      </Show>

      <Show if={() => state$.get()?.type === "brand" || state$.get()?.type === "brand-edit"}>
        {() => {
          const s = state$.get()
          if (s?.type === "brand-edit") {
            return <BrandForm onClose={() => state$.set(undefined)} id={s.id} />
          }
          return <BrandForm onClose={() => state$.set(undefined)} />
        }}
      </Show>

      <Show if={() => state$.get()?.type === "section" || state$.get()?.type === "section-edit"}>
        {() => {
          const s = state$.get()
          if (s?.type === "section-edit") {
            return <SectionForm onClose={() => state$.set(undefined)} id={s.id} initialValues={s.initialValues} />
          }
          return <SectionForm onClose={() => state$.set(undefined)} />
        }}
      </Show>

      <Show if={() => state$.get()?.type === "collection" || state$.get()?.type === "collection-edit"}>
        {() => {
          const s = state$.get()
          if (s?.type === "collection-edit") {
            return <CollectionForm onClose={() => state$.set(undefined)} sectionId={s.sectionId} id={s.id} initialValues={s.initialValues} />
          }
          if (s?.type === "collection") {
            return <CollectionForm onClose={() => state$.set(undefined)} sectionId={s.sectionId} />
          }
          return null
        }}
      </Show>
    </>
  )
}
